import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { PencilSimple, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import DecisionDialog from "@/components/approval/DecisionDialog";
import MismatchSubmitDialog from "@/components/approval/MismatchSubmitDialog";
import CorrectionRow from "./mismatchCorrection/CorrectionRow";
import useMismatchCorrections from "./mismatchCorrection/useMismatchCorrections";
import { CORRECTABLE } from "./mismatchCorrection/constants";

/**
 * MismatchCorrectionPanel — surfaces in SPBDetail when status='mismatch'.
 * Admin Kantor + Admin see a "Ajukan Koreksi" button that opens a modal
 * showing ONLY the fields flagged 'diff' by the last sync, alongside the
 * current value (SPB), the ERP value (realisasi), and an editable proposed
 * value. Requires a mandatory reason. Super Admin sees an inline
 * approve/reject action for pending corrections attached to this SPB.
 */
export default function MismatchCorrectionPanel({ spb, onSpbUpdated }) {
  const { user } = useAuth();
  const { items, loading, submitting, submit, decide, cancelDraft } =
    useMismatchCorrections(spb, onSpbUpdated);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [proposed, setProposed] = useState({});
  const [decisionDialog, setDecisionDialog] = useState(null); // { id, action }

  const isMismatch = spb?.status === "mismatch";
  const canSubmit = isMismatch && (user?.role === "admin" || user?.role === "admin_kantor");
  const isSuperAdmin = user?.role === "admin";

  const fieldStatus = spb?.realisasi?.field_status || {};
  const diffFields = CORRECTABLE.filter((f) => fieldStatus[f] === "diff");
  const pending = items.find((c) => c.status === "pending");

  const openSubmit = () => {
    // Prefill each proposed field with the ERP-reported value for convenience.
    const init = {};
    diffFields.forEach((f) => {
      init[f] = f === "distrik"
        ? spb?.realisasi?.distrik || ""
        : spb?.realisasi?.[f] ?? "";
    });
    setProposed(init);
    setReason("");
    setDialogOpen(true);
  };

  const submitCorrection = async () => {
    const ok = await submit({ proposed, reason });
    if (ok) setDialogOpen(false);
  };

  const runDecision = async (note) => {
    if (!decisionDialog) return;
    const ok = await decide({ ...decisionDialog, note });
    if (ok) setDecisionDialog(null);
  };

  if (!isMismatch && items.length === 0) return null;

  return (
    <div className="border border-amber-500/40 bg-amber-50/30 mb-8" data-testid="mismatch-correction-panel">
      <div className="p-4 border-b border-amber-500/30 bg-amber-100/40 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Warning size={16} weight="fill" className="text-amber-700" />
          <span className="text-amber-800 font-bold uppercase text-xs tracking-widest">Koreksi Mismatch</span>
          {pending && (
            <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-900 text-xs font-mono rounded-full">
              {pending.status.toUpperCase()}
            </span>
          )}
        </div>
        {canSubmit && !pending && diffFields.length > 0 && (
          <Button
            size="sm"
            variant="default"
            onClick={openSubmit}
            data-testid="mismatch-open-submit"
          >
            <PencilSimple size={14} className="mr-1" /> Ajukan Koreksi
          </Button>
        )}
      </div>

      {loading ? (
        <div className="p-4 text-sm text-muted-foreground">Memuat…</div>
      ) : items.length === 0 ? (
        <div className="p-4 text-sm text-amber-900">
          {diffFields.length > 0
            ? `${diffFields.length} field mismatch — ${canSubmit ? "klik Ajukan Koreksi untuk memperbaiki." : "menunggu tindakan admin kantor / admin."}`
            : "Belum ada rincian mismatch (mungkin sync sebelum feature ini)."}
        </div>
      ) : (
        <div className="divide-y divide-amber-500/20">
          {items.map((c) => (
            <CorrectionRow
              key={c.id}
              c={c}
              isSuperAdmin={isSuperAdmin}
              currentEmail={user?.email}
              onDecide={(action) => setDecisionDialog({ id: c.id, action })}
              onCancelDraft={() => cancelDraft(c.id)}
            />
          ))}
        </div>
      )}

      <MismatchSubmitDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        spb={spb}
        diffFields={diffFields}
        proposed={proposed}
        onProposedChange={setProposed}
        reason={reason}
        onReasonChange={setReason}
        submitting={submitting}
        onSubmit={submitCorrection}
      />

      <DecisionDialog
        open={!!decisionDialog}
        onClose={() => setDecisionDialog(null)}
        action={decisionDialog?.action}
        title={decisionDialog?.action === "approve" ? "Setujui Koreksi" : "Tolak Koreksi"}
        description={decisionDialog?.action === "approve"
          ? "Perubahan akan diterapkan ke SPB dan status jadi 'diverifikasi'."
          : "Draft koreksi ditutup, SPB tetap mismatch."}
        onConfirm={runDecision}
        submitting={submitting}
        testIdPrefix="mismatch"
      />
    </div>
  );
}
